import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useState } from "react";
import ContactFormModal from "@/components/ContactFormModal";

export default function FAQ() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I get started with a project?",
          a: "Simply reach out through our contact form or schedule a call. We'll have an initial discovery conversation to understand your needs, then provide a proposal with timeline and pricing. No commitment required for that first conversation."
        },
        {
          q: "What information do you need from me?",
          a: "We'll want to understand your business goals, target audience, current challenges, and what success looks like for you. Don't worry if you don't have all the details figured out—that's what our discovery process is for."
        },
        {
          q: "Do you work with businesses outside your local area?",
          a: "Absolutely! We work with clients across the country. Most of our communication happens via video calls, email, and project management tools, making location irrelevant."
        },
      ]
    },
    {
      category: "Pricing & Timeline",
      questions: [
        {
          q: "How much does a typical project cost?",
          a: "Project costs vary based on complexity and scope. A basic website might start around $5,000, while custom applications can range from $15,000 to $50,000+. We'll provide a detailed quote after understanding your specific needs."
        },
        {
          q: "Do you offer payment plans?",
          a: "Yes! We typically structure payments in milestones: deposit to start, progress payments during development, and final payment at launch. We can work with you to create a payment schedule that fits your budget."
        },
        {
          q: "How long does a typical project take?",
          a: "Timelines depend on project scope. A website might take 4-8 weeks, while a custom application could take 3-6 months. We'll give you a realistic timeline during the proposal phase and keep you updated throughout."
        },
        {
          q: "Can you work within my budget?",
          a: "We'll always be transparent about what's possible within your budget. If your budget is tight, we can help prioritize features and build in phases, starting with the most critical functionality."
        },
      ]
    },
    {
      category: "Process & Communication",
      questions: [
        {
          q: "How involved do I need to be during the project?",
          a: "We need your input at key milestones—initial planning, design approval, and testing. Most clients spend 2-4 hours per week on project communication. We handle all the technical work and keep you informed without overwhelming you."
        },
        {
          q: "How do you handle revisions and changes?",
          a: "Minor revisions are included in our process. For significant scope changes, we'll discuss the impact on timeline and budget before proceeding. Our goal is flexibility while keeping the project on track."
        },
        {
          q: "What if I don't like the design?",
          a: "We present designs for your approval before building. If you're not happy, we'll revise until you are. We include multiple revision rounds in our proposals to ensure you love the final product."
        },
        {
          q: "How do you keep me updated on progress?",
          a: "We provide regular updates via email and project management tools. You'll have access to a staging environment to see work in progress, and we schedule check-in calls at key milestones."
        },
      ]
    },
    {
      category: "Technical Questions",
      questions: [
        {
          q: "Will I own the code and design?",
          a: "Yes! Once the project is complete and paid for, you own everything—code, design files, and all intellectual property. We can also provide documentation and training for your team."
        },
        {
          q: "What happens if something breaks after launch?",
          a: "We provide a warranty period (typically 30-90 days) where we fix any bugs at no charge. After that, we offer maintenance plans or can fix issues on an hourly basis."
        },
        {
          q: "Can you work with my existing systems?",
          a: "In most cases, yes. We're experienced in integrating with popular platforms like CRMs, payment processors, accounting software, and more. We'll assess your current tech stack during discovery."
        },
        {
          q: "Do you provide hosting and maintenance?",
          a: "We can handle hosting and ongoing maintenance, or we can set you up with hosting and hand off management to you or your team. We're flexible based on your preferences and technical capabilities."
        },
        {
          q: "Is my data secure?",
          a: "Security is a top priority. We follow industry best practices, use encryption, implement secure authentication, and can ensure compliance with regulations like HIPAA or GDPR if needed."
        },
      ]
    },
    {
      category: "After Launch",
      questions: [
        {
          q: "Do you provide training?",
          a: "Yes! We provide training sessions for you and your team, along with documentation and video tutorials. We want you to feel confident managing your new system."
        },
        {
          q: "Can you help with marketing and SEO?",
          a: "While we're not a full-service marketing agency, we build websites with SEO best practices and can integrate with your marketing tools. We can also recommend trusted marketing partners."
        },
        {
          q: "What if I need changes or new features later?",
          a: "We're here for the long term! We can add features, make updates, or build entirely new functionality as your business grows. Many of our clients return for ongoing development."
        },
        {
          q: "Do you offer ongoing support?",
          a: "Yes, we offer monthly support and maintenance plans that include updates, security patches, backups, and priority support. Or you can engage us on an as-needed basis."
        },
      ]
    },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/20 to-background" />
          
          <div className="container relative z-10 max-w-4xl mx-auto text-center">
            <motion.h1
              className="text-5xl md:text-6xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Frequently Asked <span className="text-gradient">Questions</span>
            </motion.h1>
            
            <motion.p
              className="text-xl text-muted-foreground mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Everything you need to know about working with us. Can't find your answer? Reach out anytime.
            </motion.p>
          </div>
        </section>

        {/* FAQ Sections */}
        <section className="py-20 px-4">
          <div className="container max-w-4xl mx-auto space-y-12">
            {faqs.map((category, categoryIndex) => (
              <motion.div
                key={category.category}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: categoryIndex * 0.1 }}
              >
                <h2 className="text-3xl font-bold mb-6">{category.category}</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`${categoryIndex}-${index}`}
                      className="border border-border rounded-lg px-6 bg-card"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-4">
                        <span className="font-semibold">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Still Have Questions?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              We're here to help. Reach out and we'll get back to you within 24 hours.
            </p>
            <Button
              size="lg"
              onClick={() => setIsContactOpen(true)}
              className="bg-gradient-to-r from-primary to-secondary text-white"
            >
              Contact Us
            </Button>
          </div>
        </section>
      </div>
      <Footer />
      <ContactFormModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </>
  );
}
